import type { FC } from "react"

import styled from "styled-components"
import { fontWeight } from "styled-system"

const Wrapper = styled.div`
  --color-foreground: (--grey-dark);
  position: relative;
  padding-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 1;
  width: 100%;
  min-width: 100px;
  :focus-within {
    label {
      color: var(--blue);
    }
  }
  label {
    text-transform: capitalize;
    font-size: 12px;
    line-height: 17px;
    letter-spacing: 0.75px;
    ${fontWeight(700)};
  }
  &::after {
    content: "";
    position: absolute;
    bottom: 32px;
    right: 15px;
    width: 0;
    height: 0;
    margin: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px var(--grey-dark) solid;
    pointer-events: none;
    flex-shrink: 0;
  }
`

const StyledSelect = styled.select`
  color: var(--color-foreground);
  height: 40px;
  flex-shrink: 1;
  font-size: 14px;
  line-height: 19px;
  ${fontWeight(600)};
  box-sizing: border-box;
  width: 100%;
  padding: 8px 24px 8px 10px;
  border-bottom: 1px solid #ddd;
  background-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  &:focus {
    border-bottom: 2px solid var(--blue);
    ${fontWeight(700)};
  }
  &:disabled {
    background-color: var(--grey-light);
    border-color: #ddd;
    padding: 6px 0;
    cursor: not-allowed;
  }
`

interface ISelectProps {
  label?: string
  id?: string
  value?: string | null
  setValue: (newValue: string | null) => void
  options?: SelectOptions | string[]
  className?: string
  neutral?: boolean
  dataTestId?: string
}

export type SelectOptions = {
  label: string
  value: string
}[]

export const Select: FC<ISelectProps> = ({
  label,
  id,
  value = null,
  options,
  setValue,
  className,
  neutral,
  dataTestId,
}) => {
  return (
    <Wrapper className={className}>
      {label && <label htmlFor={id}>{label}</label>}
      {options?.length ? (
        <StyledSelect
          id={id}
          value={value ?? ``}
          onChange={(e) => setValue(e.target.value)}
          data-testid={dataTestId ?? `select`}
        >
          {neutral && (
            <option disabled value="">
              Select {label}
            </option>
          )}
          {options.map((option) => {
            const optionValue =
              typeof option === `string` ? option : option.value
            const optionLabel =
              typeof option === `string` ? option : option.label

            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            )
          })}
        </StyledSelect>
      ) : (
        <StyledSelect
          data-testid="select-empty"
          title="disabled"
          disabled={true}
        >
          No options
        </StyledSelect>
      )}
    </Wrapper>
  )
}
